import { defineComponent as _defineComponent } from 'vue'
import WidgetDefinition from '@/models/Charts/widgetDefinition';
import { BendTrendMode } from '@/models/enums/BendTrendMode';
import { FilterTimeAxisSpanEnum } from '@/models/enums/FilterTimeAxisSpanEnum';
import { WidgetEnum } from '@/models/enums/WidgetEnum';
import { dateRangeToDateStrings } from '@/utils/dates';
import { FlexibleLayoutGridDefinition } from '@/views/dashboard/flexible-layout-grid';
import FlexibleLayoutGrid from '@/views/dashboard/FlexibleLayoutGrid.vue';
import WidgetContainer from '@/components/Charts/widgetContainer/WidgetContainer.vue';
import { computed } from 'vue';
import { useI18n } from '@/composables/i18n';
import { useEchartsConnection } from '@/composables/echarts';


export default /*#__PURE__*/_defineComponent({
  __name: 'BendNewTrends',
  props: {
    selectedDateRange: null,
    selectedDateGrouping: null,
    selectedDevices: null,
    selectedShifts: null,
    selectedBendTrendMode: null
  },
  setup(__props: any) {

const props = __props as {
  selectedDateRange: [Date, Date];
  selectedDateGrouping: FilterTimeAxisSpanEnum;
  selectedDevices: string[];
  selectedShifts: number[];
  selectedBendTrendMode: BendTrendMode;
};



const layout = new FlexibleLayoutGridDefinition([
  [9, 3],
  [9, 3],
  [9, 3],
]);
const chartGroup = 'bend-new-trends';

const { t } = useI18n();
useEchartsConnection(chartGroup);

const dateRangeString = computed<[string, string]>(() =>
  dateRangeToDateStrings(props.selectedDateRange),
);

const productivityTimelineWidgetEnum = computed<WidgetEnum>(() => {
  switch (props.selectedBendTrendMode) {
    case BendTrendMode.Chronological:
      return WidgetEnum.BendTrendChronologicalProductivityTimeline;
    case BendTrendMode.Seasonal:
      return WidgetEnum.BendTrendSeasonalProductivityTimeline;
    case BendTrendMode.Comparative:
      return WidgetEnum.BendTrendComparativeProductivityTimeline;
  }
});

const productivitySummaryWidgetEnum = computed<WidgetEnum>(() => {
  switch (props.selectedBendTrendMode) {
    case BendTrendMode.Chronological:
      return WidgetEnum.BendTrendChronologicalProductivitySummary;
    case BendTrendMode.Seasonal:
      return WidgetEnum.BendTrendSeasonalProductivitySummary;
    case BendTrendMode.Comparative:
      return WidgetEnum.BendTrendComparativeProductivitySummary;
  }
});

const numberOfBendsTimelineWidgetEnum = computed<WidgetEnum>(() => {
  switch (props.selectedBendTrendMode) {
    case BendTrendMode.Chronological:
      return WidgetEnum.BendTrendChronologicalNumberOfBendsTimeline;
    case BendTrendMode.Comparative:
      return WidgetEnum.BendTrendComparativeNumberOfBendsTimeline;
    default:
      return WidgetEnum.Dummy;
  }
});

const numberOfBendsSummaryWidgetEnum = computed<WidgetEnum>(() => {
  switch (props.selectedBendTrendMode) {
    case BendTrendMode.Chronological:
      return WidgetEnum.BendTrendChronologicalNumberOfBendsSummary;
    case BendTrendMode.Comparative:
      return WidgetEnum.BendTrendComparativeNumberOfBendsSummary;
    default:
      return WidgetEnum.Dummy;
  }
});

const toolChangesTimelineWidgetEnum = computed<WidgetEnum>(() => {
  switch (props.selectedBendTrendMode) {
    case BendTrendMode.Chronological:
      return WidgetEnum.BendTrendChronologicalToolChangesTimeline;
    case BendTrendMode.Comparative:
      return WidgetEnum.BendTrendComparativeToolChangesTimeline;
    default:
      return WidgetEnum.Dummy;
  }
});

const toolChangesSummaryWidgetEnum = computed<WidgetEnum>(() => {
  switch (props.selectedBendTrendMode) {
    case BendTrendMode.Chronological:
      return WidgetEnum.BendTrendChronologicalToolChangesSummary;
    case BendTrendMode.Comparative:
      return WidgetEnum.BendTrendComparativeToolChangesSummary;
    default:
      return WidgetEnum.Dummy;
  }
});

const productionOutputTimelineWidgetEnum = computed<WidgetEnum>(() => {
  switch (props.selectedBendTrendMode) {
    case BendTrendMode.Chronological:
      return WidgetEnum.BendTrendChronologicalProductionOutputTimeline;
    case BendTrendMode.Comparative:
      return WidgetEnum.BendTrendComparativeProductionOutputTimeline;
    default:
      return WidgetEnum.Dummy;
  }
});

const productionOutputSummaryWidgetEnum = computed<WidgetEnum>(() => {
  switch (props.selectedBendTrendMode) {
    case BendTrendMode.Chronological:
      return WidgetEnum.BendTrendChronologicalProductionOutputSummary;
    case BendTrendMode.Comparative:
      return WidgetEnum.BendTrendComparativeProductionOutputSummary;
    default:
      return WidgetEnum.Dummy;
  }
});

const widgetDefinitions = computed<WidgetDefinition[]>(() =>
  [
    // new WidgetDefinition(
    //   productivityTimelineWidgetEnum.value,
    //   props.selectedDevices,
    //   dateRangeString.value,
    //   props.selectedShifts,
    //   undefined,
    //   props.selectedDateGrouping,
    // )
    //   .hideInfo()
    //   .withSubtitle(t('bend_trend_productivity.subtitle')),
    // new WidgetDefinition(
    //   productivitySummaryWidgetEnum.value,
    //   props.selectedDevices,
    //   dateRangeString.value,
    //   props.selectedShifts,
    // ),
    new WidgetDefinition(
      numberOfBendsTimelineWidgetEnum.value,
      props.selectedDevices,
      dateRangeString.value,
      props.selectedShifts,
      undefined,
      props.selectedDateGrouping,
    )
      .withSubtitle(t('bend_trend_number_of_bends.subtitle').toString())
      .hideInfo()
      .withGroup(chartGroup),
    new WidgetDefinition(
      numberOfBendsSummaryWidgetEnum.value,
      props.selectedDevices,
      dateRangeString.value,
      props.selectedShifts,
      undefined,
      props.selectedDateGrouping,
    ),
    new WidgetDefinition(
      toolChangesTimelineWidgetEnum.value,
      props.selectedDevices,
      dateRangeString.value,
      props.selectedShifts,
      undefined,
      props.selectedDateGrouping,
    )
      .withSubtitle(t('bend_trend_tool_changes.subtitle').toString())
      .hideInfo()
      .withGroup(chartGroup),
    new WidgetDefinition(
      toolChangesSummaryWidgetEnum.value,
      props.selectedDevices,
      dateRangeString.value,
      props.selectedShifts,
      undefined,
      props.selectedDateGrouping,
    ),
    new WidgetDefinition(
      productionOutputTimelineWidgetEnum.value,
      props.selectedDevices,
      dateRangeString.value,
      props.selectedShifts,
      undefined,
      props.selectedDateGrouping,
    )
      .withSubtitle(t('bend_trend_production_output.subtitle').toString())
      .hideInfo()
      .withGroup(chartGroup),
    new WidgetDefinition(
      productionOutputSummaryWidgetEnum.value,
      props.selectedDevices,
      dateRangeString.value,
      props.selectedShifts,
      undefined,
      props.selectedDateGrouping,
    ),
  ].map((wd, index) => wd.withIndex(index)),
);

return { __sfc: true,props, layout, chartGroup, t, dateRangeString, productivityTimelineWidgetEnum, productivitySummaryWidgetEnum, numberOfBendsTimelineWidgetEnum, numberOfBendsSummaryWidgetEnum, toolChangesTimelineWidgetEnum, toolChangesSummaryWidgetEnum, productionOutputTimelineWidgetEnum, productionOutputSummaryWidgetEnum, widgetDefinitions, FlexibleLayoutGrid, WidgetContainer }
}

})